import React from 'react';
import { AlertTriangle, CheckSquare, Clock, Server, Zap } from 'lucide-react';

const parseNumericTime = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

// Convierte tiempos en formato HH:MM[:SS] o horas decimales a segundos
const timeToSeconds = (timeValue) => {
  if (timeValue === null || timeValue === undefined || timeValue === '') return 0;

  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const parts = timeValue.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return (hours * 3600) + (minutes * 60) + seconds;
  }

  const numericHours = parseNumericTime(timeValue);
  return numericHours !== null ? numericHours * 3600 : 0;
};

// Convierte HH:MM[:SS] o horas decimales a horas
const timeToHours = (timeValue) => {
  if (timeValue === null || timeValue === undefined || timeValue === '') return 0;

  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const parts = timeValue.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return hours + minutes / 60 + seconds / 3600;
  }

  const numericHours = parseNumericTime(timeValue);
  return numericHours !== null ? numericHours : 0;
};

const KPICards = ({ tickets = [], alerts = [], cardClasses = '', dateFilter = '' }) => {
  // --- SLA de Resolución según Prioridad
  const SLA_RESOLUTION = {
    "Critica": 4 * 3600,
    "Alta": 8 * 3600,
    "Media": 12 * 3600,
    "Baja": 24 * 3600
  };
  
  // Cálculos para métricas
  const totalTickets = tickets.length;

  // --- Cálculo de SLA Primera Respuesta Domo
  // --- Filtramos primero los tickets válidos (cerrados o solucionados)
  const ticketsValidos = tickets.filter(t => 
    (
      t.Estado_de_solicitud === "Cerrado" ||
      t.Estado_de_solicitud === "Solucionado" ||
      t.Estado_de_solicitud === "Pendiente por usuario"
    )
    && t.Tipo_de_ticket !== "Incidente"
  );

  const slaDomoOk = ticketsValidos.filter(t => {
    const responseTranscurrido = timeToSeconds(t.Tiempo_de_respuesta_transcurrido);
    // El backend ya envía SLA válidos. Si es 0 por cualquier razón, usar fallback
    let responseSLA = timeToSeconds(t.Tiempo_de_respuesta_de_SLA);
    if (responseSLA === 0) {
      responseSLA = 1 * 60 * 60; // 1 hora por defecto
    }
    return responseTranscurrido <= responseSLA;
  }).length;
  
  const totalTicketsValidos = ticketsValidos.length;

  // const slaDomoPct = totalTicketsValidos > 0 
  //   ? ((slaDomoOk / totalTicketsValidos) * 100).toFixed(2)
  //   : 0;
  const slaDomoPct = totalTicketsValidos > 0 
    ? (Math.floor((slaDomoOk / totalTicketsValidos) * 10000) / 100).toFixed(2)
    : "0.00";


  // --- Cálculo de SLA Resolución
  const slaResolutionOk = tickets.filter(t => {
    const resolutionSecs = timeToSeconds(t.Tiempo_transcurrido);
    const priority = t.Prioridad || "Media";
    const threshold = SLA_RESOLUTION[priority] || SLA_RESOLUTION["Media"];
    return resolutionSecs <= threshold;
  }).length;

  const slaResolutionPct = totalTickets > 0 
    ? ((slaResolutionOk / totalTickets) * 100).toFixed(1)
    : 0;
  
  const avgResponseTime = tickets.length > 0 ? 
  (
    tickets.reduce((sum, t) => sum + timeToHours(t.Tiempo_de_respuesta_transcurrido), 0) / tickets.length
  ).toFixed(1) : 0;

  const totalAlerts = alerts.reduce((sum, a) => sum + (a.Cantidad || 0), 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
      <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">Total Tickets</p>
            <p className="text-3xl font-bold mt-1">{totalTickets}</p>
            <p className="text-sm text-green-400 mt-1">{dateFilter}</p>
          </div>
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      {/* <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">SLA Primera Respuesta</p>
            <p className="text-3xl font-bold mt-1">{slaFirstResponsePct}%</p>
            <p className="text-sm text-green-400 mt-1">Objetivo: 99%</p>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-3 rounded-xl">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
        </div>
      </div> */}

      <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">SLA Primera Respuesta Domo</p>
            <p className="text-3xl font-bold mt-1">{slaDomoPct}%</p>
            <p className="text-sm text-green-400 mt-1">Objetivo: 99%</p>
          </div>
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-3 rounded-xl">
            <Zap className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">SLA Resolucion</p>
            <p className="text-3xl font-bold mt-1">{slaResolutionPct}%</p>
            <p className="text-sm text-green-400 mt-1">Objetivo: 99%</p>
          </div>
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-3 rounded-xl">
            <CheckSquare className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">Tiempo Promedio</p>
            <p className="text-3xl font-bold mt-1">{avgResponseTime}h</p>
            <p className="text-sm text-yellow-400 mt-1">Meta: 2h</p>
          </div>
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-3 rounded-xl">
            <Clock className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <div className={`${cardClasses} p-6 rounded-2xl hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-70 font-medium">Total de Alertas</p>
            <p className="text-3xl font-bold mt-1">{totalAlerts}</p>
            <p className="text-sm text-red-400 mt-1">{dateFilter}</p>
          </div>
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-3 rounded-xl">
            <Server className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPICards;